import { useState, useEffect } from 'react';
import { FileText } from 'lucide-react';
import { getTransformedSignedUrl } from '@/utils/storageImages';
import type { StakeholderAttachment } from '@/pages/admin/StakeholderManagement';

interface Props {
  attachments?: StakeholderAttachment[];
  stakeholderId: string;
  index?: number; // ✅ Optional index for priority hints
  size?: 'small' | 'medium' | 'large'; // ✅ Size variants for different contexts
  editable?: boolean; // ✅ Enable click-to-edit
  onEdit?: () => void; // ✅ Edit callback
}

// ✅ Global signed URL cache (outside component to persist across renders)
const signedUrlCache = new Map<string, { url: string; expires: number }>();

// ✅ Get cached signed URL with transform or fetch new one (cache for 50 minutes)
const getCachedSignedUrl = async (path: string): Promise<string> => {
  const cached = signedUrlCache.get(path);
  
  // Return cached URL if still valid
  if (cached && cached.expires > Date.now()) {
    return cached.url;
  }
  
  // Fetch new signed URL with transform (expires in 1 hour)
  const url = await getTransformedSignedUrl(
    'stakeholder-attachments',
    path,
    3600,
    {
      width: 80,
      height: 80,
      quality: 70,
      resize: 'cover'
    }
  );
  
  // Cache for 50 minutes (3000 seconds)
  signedUrlCache.set(path, { 
    url, 
    expires: Date.now() + 50 * 60 * 1000 
  });
  
  return url;
};

// Helper: Retry fetch with 300ms delay
const fetchThumbnailWithRetry = async (path: string, retries = 1): Promise<string> => {
  try {
    return await getCachedSignedUrl(path); // ✅ Use cached version
  } catch (err) {
    if (retries > 0) {
      console.warn('[StakeholderThumbnail] Retrying fetch for path:', path);
      await new Promise(resolve => setTimeout(resolve, 300));
      return fetchThumbnailWithRetry(path, retries - 1);
    }
    throw err;
  }
};

export const StakeholderThumbnail = function StakeholderThumbnail({ 
  attachments, 
  stakeholderId,
  index = 999, // ✅ Default high value for priority calculation
  size = 'small', // ✅ Default to small size
  editable = false, // ✅ Disabled by default
  onEdit // ✅ Optional edit callback
}: Props) {
  // ✅ Size class mapping
  const sizeClasses = {
    small: 'w-10 h-10',
    medium: 'w-16 h-16',
    large: 'w-24 h-24'
  };
  const sizeClass = sizeClasses[size];
  const [thumbnailUrl, setThumbnailUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false); // ✅ Track errors for fallback

  useEffect(() => {
    let isMounted = true;
    setIsLoading(true);
    setHasError(false); // ✅ Reset error on new fetch

    // 1) Không có attachments → clear thumbnailUrl ngay
    if (!attachments?.length) {
      if (isMounted) {
        setThumbnailUrl(null);
        setIsLoading(false);
      }
      return;
    }

    // 2) Không có file ảnh → clear thumbnailUrl ngay
    const imageFile = attachments.find(att => att.type?.startsWith('image/'));
    if (!imageFile) {
      if (isMounted) {
        setThumbnailUrl(null);
        setIsLoading(false);
      }
      return;
    }

    // 3) Tạo version string cho cache-buster
    const version = [imageFile.path, imageFile.uploadedAt, imageFile.size]
      .filter(Boolean)
      .join('_');

    // 4) Fetch signed URL + append cache-buster
    fetchThumbnailWithRetry(imageFile.path)
      .then((signedUrl) => {
        if (!isMounted) return;
        const sep = signedUrl.includes('?') ? '&' : '?';
        setThumbnailUrl(`${signedUrl}${sep}v=${encodeURIComponent(version)}`);
      })
      .catch((err) => {
        console.error('[StakeholderThumbnail] Failed after retry:', imageFile.path, err);
        if (isMounted) {
          setThumbnailUrl(null);
          setHasError(true); // ✅ Mark as error for fallback display
        }
      })
      .finally(() => {
        if (isMounted) {
          setIsLoading(false);
        }
      });

    // 5) Cleanup: prevent setState on unmounted component
    return () => { 
      isMounted = false; 
    };
  }, [attachments, stakeholderId]);

  // Editable wrapper classes
  const editableClasses = editable ? 'cursor-pointer hover:ring-2 hover:ring-primary/50 transition-all' : '';

  // ✅ Progressive loading skeleton
  if (isLoading) {
    return (
      <div 
        className={`relative ${sizeClass} rounded-full border border-border overflow-hidden bg-muted ${editableClasses}`}
        onClick={editable ? onEdit : undefined}
      >
        {/* Skeleton pulse animation */}
        <div className="absolute inset-0 bg-gradient-to-r from-muted via-muted-foreground/10 to-muted animate-pulse" />
        
        {/* Subtle spinner overlay */}
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-4 h-4 border-2 border-muted-foreground/20 border-t-muted-foreground/60 rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  // ✅ Enhanced error state with visual feedback
  if (hasError && attachments?.length) {
    return (
      <div 
        className={`relative ${sizeClass} rounded-full border border-destructive/30 overflow-hidden bg-destructive/5 ${editableClasses}`}
        title={editable ? 'Click to upload image' : 'Failed to load image'}
        onClick={editable ? onEdit : undefined}
      >
        {/* Error icon */}
        <div className="absolute inset-0 flex items-center justify-center">
          <FileText className="h-5 w-5 text-destructive/60" />
        </div>
        
        {/* Error badge */}
        <div className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-destructive border border-background" />
      </div>
    );
  }

  // Has image thumbnail
  if (thumbnailUrl) {
    return (
      <div 
        className={`relative ${sizeClass} rounded-full overflow-hidden ${editableClasses}`}
        onClick={editable ? onEdit : undefined}
        title={editable ? 'Click to change image' : undefined}
      >
        <img
          src={thumbnailUrl}
          alt={`${stakeholderId.slice(0, 8)} avatar`}
          className={`${sizeClass} object-cover rounded-full border border-border`}
          loading={index < 10 ? 'eager' : 'lazy'} // ✅ First 10 eager
        />
      </div>
    );
  }

  // Has attachments but no images
  const hasFiles = attachments && attachments.length > 0;
  if (hasFiles) {
    return (
      <div 
        className={`${sizeClass} bg-muted rounded-full flex items-center justify-center text-muted-foreground ${editableClasses}`}
        title={editable ? 'Click to upload image' : attachments.map(a => a.name).join(', ')}
        onClick={editable ? onEdit : undefined}
      >
        <FileText className="h-4 w-4" />
      </div>
    );
  }

  // No attachments
  return (
    <div 
      className={`${sizeClass} flex items-center justify-center text-muted-foreground border border-dashed border-border rounded-full ${editableClasses}`}
      onClick={editable ? onEdit : undefined}
      title={editable ? 'Click to upload image' : undefined}
    >
      <span className="text-xs">{editable ? '+' : '-'}</span>
    </div>
  );
};
